import { useEffect, useRef } from 'react';

import useUserStore from '@lcsc-ui/store/user';
import sendAxios from '@lcsc-ui/utils/request';

import Api from '@/apis';
import { useWasm } from '@/hooks/useWasm';
import useProductStore from '@/store/product';
import { env } from '@/utils/baseUrl';

// 验签错误码常量
const SIGNATURE_ERROR_CODES = {
  NONCE_EXPIRE: 100103, // nonce过期 - 重新加载WASM模块
};

// 流控错误码常量
const FLOW_CONTROL_CODES = {
  NO_CID: 110101, // 未携带cid - 重新调用验签接口
  RATE_LIMIT: 110102, // 触发流控 - 显示友好提示
  NO_CVT: 110201, // 未携带cvt - 重新调用验签接口
  BAD_CVT: 110202, // cvt验证失败 - 重新调用验签接口
};

export const useProductAsyncDataEffect = (webData) => {
  const { wasmModule, isLoading, reload: reloadWasm } = useWasm();
  const { updateProductAsyncData, updateFlowControlState, flowControlState } = useProductStore();
  const { userInfo } = useUserStore();
  const hasExecutedRef = useRef(false);
  const cvtCacheRef = useRef(null); // 降级存储

  const storageKey = `lcb_cvt_${env}`;

  // 安全的 localStorage 操作
  const safeSetItem = (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      // localStorage 满了或不可用，降级到内存存储
      cvtCacheRef.current = value;
      return false;
    }
  };

  const safeGetItem = (key) => {
    try {
      return localStorage.getItem(key) || cvtCacheRef.current;
    } catch (e) {
      return cvtCacheRef.current;
    }
  };

  // 检查是否存在流控 cvt
  const checkFlowControlCvt = () => {
    return !!safeGetItem(storageKey);
  };

  // 清除流控 cvt
  const clearFlowControlCvt = () => {
    localStorage.removeItem(storageKey);
  };

  // 请求流控接口
  const requestFlowControl = async () => {
    if (!wasmModule) return false;

    try {
      const cid = wasmModule.generate_cid();
      const params = { cid };
      const nonce = wasmModule.generate_nonce();
      const sign = wasmModule.generate_sign(Object.entries(params));
      params.nonce = nonce;
      params.sign = sign;

      const queryString = new URLSearchParams(params);

      const res = await sendAxios({
        method: 'POST',
        url: `${Api.cidVerify}?${queryString}`,
        baseURL: env === 'local' ? 'https://devitem.szlcsc.com' : 'lcscItemUrl',
      });

      if (res?.code === 200 && res?.result) {
        safeSetItem(storageKey, res.result);
        return true;
      }

      return false;
    } catch (error) {
      console.error('流控接口请求失败:', error);
      return false;
    }
  };

  // 主要的执行流程
  const executeFlow = async () => {
    // 如果有流控限制，跳过执行
    if (flowControlState.rateLimitInfo) return;

    let flowControlSuccess = true;

    // 检查是否需要请求流控接口
    if (!checkFlowControlCvt()) {
      flowControlSuccess = await requestFlowControl();
    }

    if (!flowControlSuccess || !wasmModule) return;

    // 直接在这里处理数据获取，避免循环依赖
    try {
      const params = {
        productId: webData?.productRecord?.productId,
        cvt: safeGetItem(storageKey),
      };

      if (userInfo?.needSign !== false) {
        const nonce = wasmModule.generate_nonce();
        const sign = wasmModule.generate_sign(Object.entries(params));
        params.nonce = nonce;
        params.sign = sign;
      }

      const queryString = new URLSearchParams(params);

      let res = null;
      if (env === 'local') {
        const response = await fetch(`/api/encrypted-data?${queryString}`);
        res = await response.json();
      } else {
        res = await sendAxios({
          method: 'GET',
          url: `${Api.productStockPriceSign}?${queryString}`,
          baseURL: 'lcscItemUrl',
          data: params,
        });
      }

      // 检查验签错误码
      if (Object.values(SIGNATURE_ERROR_CODES).includes(res?.code)) {
        handleSignatureError(res.code);
        return;
      }

      // 检查流控错误码
      if (Object.values(FLOW_CONTROL_CODES).includes(res?.code)) {
        handleFlowControlError(res.code, res?.result);
        return;
      }

      if (res?.code === 200) {
        let productShowResult = null;

        if (res?.result?.productShowResultSignStr?.indexOf('lcsc_encrypt_v1:') === 0) {
          const encryptedData = res?.result?.productShowResultSignStr;
          const decryptedData = wasmModule.decrypt_data(encryptedData);
          productShowResult = JSON.parse(decryptedData);
        } else {
          productShowResult = JSON.parse(res?.result?.productShowResultSignStr);
        }

        updateProductAsyncData({ ...productShowResult, isFetched: true });
        updateFlowControlState({ rateLimitInfo: null });
      }
    } catch (error) {
      console.error('获取解密数据异常:', error);
    }
  };

  // 处理流控错误的内部函数
  function handleFlowControlError(code, result) {
    switch (code) {
      case FLOW_CONTROL_CODES.NO_CID:
      case FLOW_CONTROL_CODES.NO_CVT:
      case FLOW_CONTROL_CODES.BAD_CVT: {
        clearFlowControlCvt();
        executeFlow();
        break;
      }
      case FLOW_CONTROL_CODES.RATE_LIMIT: {
        const waitTime = result || 30000;
        updateFlowControlState({
          rateLimitInfo: {
            waitTime,
          },
        });
        setTimeout(() => {
          updateFlowControlState({ rateLimitInfo: null });
          executeFlow();
        }, waitTime);
        break;
      }
    }
  }

  // 处理签名验证错误的内部函数
  async function handleSignatureError(code) {
    if (code === SIGNATURE_ERROR_CODES.NONCE_EXPIRE) {
      await reloadWasm();
      executeFlow();
    }
  }

  useEffect(() => {
    if (isLoading || !wasmModule || hasExecutedRef.current) return;

    hasExecutedRef.current = true;
    executeFlow();
  }, [isLoading, wasmModule]);
};
